import {
	type AvailableEntitiesResponse,
	type AvailableFieldsResponse,
	type FieldKey,
	type Filter,
	type FilterResponse,
	type FilterValues,
	type MetricEntityType,
	type MetricParameters,
	type MetricResponse,
	type MetricValues,
	type MetricValueTaxonomy,
	type PresetResponse,
	type ReferenceEntityValues,
	type RiskModelExposureType,
	type UserInstrumentClassificationDto,
	MetricOrdering,
	MetricReferenceType,
	MetricThresholdType,
} from "@eu.mdotm.api/sphere";
import { reportPlatformError, runWithErrorReporting } from "$root/api/error-reporting";
import {
	createLeafPathFromValue,
	DatePickerInput,
	type DiscriminateHierarchicalNodeOption,
	type HierarchicalOption,
	MultipleHierarchicalSelect,
	Button,
	IconButton,
	FormField,
	NumberInput,
	Select,
	type SelectOption,
	type SelectOptions,
} from "@mdotm/shadcn/mdotm/components";
import OverlayActions from "$root/components/OverlayActions";
import ReactQueryWrapper, { ReactQueriesWrapperBase, ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import Separator from "$root/components/Separator";
import { SideDrawer } from "$root/components/common/SideDrawer";
import { spawnCreateDialog } from "$root/components/spawnable/entity-management/create-dialog";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { platformToast } from "$root/notification-system/toast";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { ToastableError } from "$root/utils";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { minutes } from "$root/utils/time";
import {
	AutoTooltip,
	ButtonGroup,
	CircularProgressBar,
	Controller,
	DebouncedSearchInput,
	Icon,
	Row,
	Transition,
} from "@mdotm/shadcn/legacy/components";
import {
	type ImperativeHandleRefProps,
	generateUniqueDOMId,
	overrideClassName,
	useRefProxyWithState,
	useTick,
} from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { readAtom, useAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type SetterOrUpdaterParam, chunk, maybeCall, noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { groupBy } from "@mdotm/utils/collections";
import { useQueries } from "@tanstack/react-query";
import { hash, Map, OrderedMap } from "immutable";
import { useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { bulkAnalysisRepository } from "../portfolio/bulk-analysis-repository";
import {
	type keyMetricThresholdMapType,
	type PortfolioInsightPanelPreset,
	emptyInsightPanelPreset,
	insightPanelSession$,
} from "./insightPanelSession";
import { userClassifications$ } from "../instruments/user-classifications";
import { t } from "i18next";

export const extendedTabWidth = 464;
export const smallTabWidth = 24;

function handleMapTruthy<K, T>(map?: Map<K, T>): Map<K, T> {
	return map ?? Map();
}

export type PortfolioInsightPanelProps = {
	expand: boolean;
	onExpandChange(): void;
	onChangePreset(preset?: PortfolioInsightPanelPreset): void;
};

export function convertFilterResponseToFilter(filterResponse: Array<FilterResponse>): Array<Filter> {
	return filterResponse
		.filter((x) => x.key && x.value && x.relation)
		.map((x) => ({ fieldKey: x.key, relation: x.relation, value: x.value }));
}

function parsePresetResponse(preset?: PresetResponse): PortfolioInsightPanelPreset {
	return preset
		? {
				...preset,
				...emptyInsightPanelPreset,
				filters: Map<string, Filter>(preset.filters?.map((x) => [generateUniqueDOMId(), x])),
				metric: {
					...preset.metric,
					keyMetricThreshold: Map<string, keyMetricThresholdMapType>([
						[
							generateUniqueDOMId(),
							preset.metric?.thresholdType === "MANUAL"
								? {
										thresholdType: preset.metric?.thresholdType,
										thresholdValue: preset.metric?.thresholdValue ?? 0,
									}
								: {
										thresholdType: preset.metric?.thresholdType,
										thresholdEntity: preset.metric?.thresholdEntity,
									},
						],
					]),
				},
			}
		: emptyInsightPanelPreset;
}

export type PortfolioInsightPanelHandles = {
	setThreshold(thresholdValue?: number): void;
	setManualSelection(manualSelection?: Array<string>): void;
	setOrder(newOrder: MetricResponse["ordering"]): void;
};

function PresetOption(props: {
	preset: PresetResponse;
	isOpen: boolean;
	isFavorite: boolean;
	onToggleOverlay(isOpen: boolean): void;
	onCheckIfNameIsAvailable: (name: string) => Promise<boolean>;
	onRenamePreset(name: string): Promise<void>;
	onDeletePreset(): Promise<void>;
	onToggleFavoritePreset?(isFavorite: boolean): Promise<void>;
}) {
	const quickActionsRef = useRef<HTMLElement>(null);
	const [quickActions, setQuickActions] = useRefProxyWithState(null, quickActionsRef);

	const { t } = useTranslation();

	return (
		<Row alignItems="center">
			<IconButton
				icon={props.isFavorite ? "Star-Full" : "Star-empty"}
				style={{ color: themeCSSVars.palette_N400 }}
				disabled={props.onToggleFavoritePreset === undefined}
				onClickAsync={async (e) => {
					e.stopPropagation();
					await runWithErrorReporting(
						async () => {
							try {
								await props.onToggleFavoritePreset?.(!props.isFavorite);
							} catch (err) {
								throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: `change favorite ${props.preset.name!} preset as ${props.isFavorite}`,
								payload: JSON.stringify({ label: props.preset.name!, value: props.preset.uuid! }),
							},
						},
					);
					props.onToggleOverlay(false);
				}}
			/>
			<span className="text-start block grow truncate relative z-0 text-[14px]">{props.preset.name!}</span>
			{!props.preset.standard && (
				<div className="relative">
					<IconButton
						icon="More"
						size={20}
						color={themeCSSVars.palette_N400}
						innerRef={setQuickActions}
						onClick={(e) => {
							e.stopPropagation();
							props.onToggleOverlay(true);
						}}
					/>

					<OverlayActions
						open={props.isOpen}
						triggerEl={quickActions}
						mode="manual"
						actions={[
							{
								icon: "Edit",
								iconColor: themeCSSVars.palette_N400,
								onClick(e) {
									e.stopPropagation();
									props.onToggleOverlay(false);
									spawnRenameDialog({
										currentName: props.preset.name!,
										entityType: "Preset",
										checkIfNameIsAvailable: props.onCheckIfNameIsAvailable,
										onSubmitAsync(name) {
											return runWithErrorReporting(
												async () => {
													try {
														await props.onRenamePreset(name);

														platformToast({
															children: `You have successfully rename ${name} preset`,
															severity: "success",
															icon: "Portfolio",
														});

														return name;
													} catch (err) {
														throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
													}
												},
												{
													area: "portfolio",
													attemptedOperation: {
														message: `delete ${props.preset.name!} preset`,
														payload: JSON.stringify({ label: props.preset.name!, value: props.preset.uuid! }),
													},
												},
											);
										},
									}).catch(noop);
								},
							},
							{
								icon: "Delete",
								iconColor: themeCSSVars.palette_N400,
								onClick(e) {
									e.stopPropagation();
									props.onToggleOverlay(false);
									spawnDeleteDialog({
										entityType: "Preset",
										entityName: props.preset.name!,
										onDeleteAsync() {
											return runWithErrorReporting(
												async () => {
													try {
														await props.onDeletePreset();

														platformToast({
															children: `You have successfully delete ${props.preset.name!} preset`,
															severity: "success",
															icon: "Portfolio",
														});
													} catch (err) {
														throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
													}
												},
												{
													area: "portfolio",
													attemptedOperation: {
														message: `rename ${props.preset.name!} preset`,
														payload: JSON.stringify({ label: props.preset.name!, value: props.preset.uuid! }),
													},
												},
											);
										},
									}).catch(noop);
								},
							},
						]}
					/>
				</div>
			)}
		</Row>
	);
}

function SelectPreset(props: {
	value: string;
	presets?: PresetResponse[];
	onChangeSelection?(newPreset: PortfolioInsightPanelPreset, uuid?: string | null): void;
	onCheckIfNameIsAvailable: (name: string) => Promise<boolean>;
	onRenamePreset(name: string, preset: PresetResponse): Promise<void>;
	onDeletePreset(preset: PresetResponse): Promise<void>;
	onToggleFavoritePreset?(isFavorite: boolean, preset: PresetResponse): Promise<void>;
}) {
	const { presets, value, onChangeSelection } = props;
	const [activeOverlay, setActiveOverlay] = useState<null | string>(null);

	const options = useMemo(() => {
		if (!presets) {
			return [];
		}
		return presets.flatMap(
			(x): Array<SelectOption<string>> =>
				x.uuid && x.name
					? [
							{
								label: x.name,
								value: x.uuid,
								children: () => (
									<PresetOption
										preset={x}
										isFavorite={x.favorite ?? false}
										isOpen={activeOverlay === x.uuid}
										onToggleOverlay={(isOpen) => setActiveOverlay(isOpen ? x.uuid! : null)}
										onCheckIfNameIsAvailable={props.onCheckIfNameIsAvailable}
										onRenamePreset={(name) => props.onRenamePreset(name, x)}
										onDeletePreset={() => props.onDeletePreset(x)}
										onToggleFavoritePreset={
											props.onToggleFavoritePreset
												? (isFavorite) => props.onToggleFavoritePreset!(isFavorite, x)
												: undefined
										}
									/>
								),
							} satisfies SelectOption<string>,
						]
					: [],
		);
	}, [presets, activeOverlay, props]);

	const presetMap = useMemo(() => {
		if (!presets) {
			return Map<string, PortfolioInsightPanelPreset | undefined>();
		}
		return Map<string, PortfolioInsightPanelPreset | undefined>(
			presets.flatMap((x) => (x.uuid ? [[x.uuid, parsePresetResponse(x)]] : [])),
		);
	}, [presets]);

	const onSelectPreset = useCallback(
		(uuid: string) => {
			onChangeSelection?.(presetMap.get(uuid) ?? emptyInsightPanelPreset, uuid);
		},
		[onChangeSelection, presetMap],
	);
	return (
		<Select
			data-qualifier="InsightPanel/SelectPreset/Dropdown"
			triggerDataAttrs={{
				"data-qualifier": "InsightPanel/SelectPreset/Trigger",
			}}
			options={options}
			value={value}
			onChange={(uuid) => {
				if (uuid) {
					onSelectPreset(uuid);
				}
			}}
			className="w-[150px]"
		/>
	);
}

function PortfolioInsightPanel(
	props: PortfolioInsightPanelProps & ImperativeHandleRefProps<PortfolioInsightPanelHandles>,
): React.JSX.Element {
	const { onChangePreset, onExpandChange, handleRef, expand } = props;

	const [insightPanelPresetRaw, setInsightPanelPresetRaw] = useAtom(insightPanelSession$);
	const insightPanelPreset = insightPanelPresetRaw ?? emptyInsightPanelPreset;
	const lastInsightPanelPresetRef = useRef(insightPanelPreset);
	const setInsightPanelPreset = useCallback(
		(valueOrUpdater: SetterOrUpdaterParam<PortfolioInsightPanelPreset>) => {
			const cur = readAtom(insightPanelSession$) ?? emptyInsightPanelPreset;
			const next = maybeCall(valueOrUpdater, cur);
			lastInsightPanelPresetRef.current = next;
			setInsightPanelPresetRaw(next);
		},
		[setInsightPanelPresetRaw],
	);

	const { t } = useTranslation();
	useImperativeHandle(
		handleRef,
		() => ({
			setThreshold: (threshold) => {
				trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.keyMetric.threshold", null);
				setInsightPanelPreset((preset) => {
					const key = Array.from(preset.metric?.keyMetricThreshold?.keys() ?? []).at(0);
					return {
						...preset,
						metric: {
							...preset.metric,
							keyMetricThreshold: handleMapTruthy(preset.metric?.keyMetricThreshold).set(key ?? generateUniqueDOMId(), {
								thresholdType: "MANUAL",
								thresholdValue: threshold ?? 0,
							}),
						},
					};
				});
				onChangePreset(lastInsightPanelPresetRef.current);
			},
			setManualSelection: (manualSelection) => {
				trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.filters.change", null);
				setInsightPanelPreset((preset) => {
					const presetMap = handleMapTruthy(preset.filters);
					const key = presetMap.findKey((v) => v.key === "NAME");
					return {
						...preset,
						filters: presetMap.set(key ?? generateUniqueDOMId(), {
							key: "NAME",
							type: "FINITE_SET_PORTFOLIOS",
							relation: "EQUAL",
							value: manualSelection,
						}),
					};
				});

				onChangePreset(lastInsightPanelPresetRef.current);
			},
			setOrder: (newOrder) => {
				setInsightPanelPreset((preset) => {
					if (preset.metric) {
						preset.metric.ordering = newOrder;
					}
					return preset;
				});
			},
		}),
		[onChangePreset, setInsightPanelPreset],
	);

	const queryPresets = bulkAnalysisRepository.presets.useQuery();
	useQueryReactor(queryPresets, {
		onSuccess({ currentPreset }) {
			const parsedPreset = parsePresetResponse(currentPreset);
			const mergedPreset = { ...parsedPreset, ...insightPanelPresetRaw };
			setInsightPanelPreset(mergedPreset);
			onChangePreset(mergedPreset);
		},
		onError(err) {
			reportPlatformError(err, "ERROR", "portfolio");
			setInsightPanelPreset(emptyInsightPanelPreset);
			onChangePreset(emptyInsightPanelPreset);
		},
	});

	const queryCommonSelectableFilters = bulkAnalysisRepository.availableFields.useQuery();

	const userClassifications = useAtomValue(userClassifications$);
	const queriesCustomKeyMetrics = useQueries({
		queries: [
			{
				...bulkAnalysisRepository.availableEntities.getQueryConfig(),
			},
			{
				...bulkAnalysisRepository.riskModelExposureTypes.getQueryConfig(),
				staleTime: 0,
				cacheTime: minutes(10),
			},
		],
	});

	const metricBlockHash = useMemo(() => hash(insightPanelPreset.uuid), [insightPanelPreset.uuid]);

	const onSaveAsNewPreset = useCallback(async (currentPreset: PortfolioInsightPanelPreset, name: string) => {
		await runWithErrorReporting(
			async () => {
				try {
					const { search: _search, metric, filters, uuid: _uuid, ...params } = currentPreset;
					const { keyMetricThreshold, ...reset } = metric ?? {};
					const keyMetricThresholdValues = keyMetricThreshold?.first();
					await bulkAnalysisRepository.savePreset({
						...params,
						name,
						standard: false,
						favourite: false,
						filters: convertFilterResponseToFilter(Array.from(filters?.values() ?? [])),
						metric: {
							...reset,
							thresholdEntity: keyMetricThresholdValues?.thresholdEntity,
							thresholdType: keyMetricThresholdValues?.thresholdType,
							thresholdValue: keyMetricThresholdValues?.thresholdValue,
						},
					});
				} catch (error) {
					throw new ToastableError("Something went wrong please try later", { icon: "Portfolio", cause: error });
				}
			},
			{ area: "portfolio", attemptedOperation: { message: "try to save preset as new" } },
		);
	}, []);

	return (
		<SideDrawer
			toggleIcon="Filter-no"
			toggleOffset={64}
			expand={expand}
			onExpandChange={onExpandChange}
			widths={[smallTabWidth, extendedTabWidth]}
			title="Analysis panel"
			actionHeader={
				<ReactQueryWrapperBase
					query={queryPresets}
					errorFallback={<Select value="" options={[]} disabled className="w-[150px]" />}
					loadingFallback={<CircularProgressBar value="indeterminate" outerDiameter={16} innerDiameter={12} />}
				>
					{({ presets }, rq) => (
						<SelectPreset
							value={insightPanelPreset.uuid!}
							presets={presets}
							onChangeSelection={(newPreset) => {
								setInsightPanelPreset(newPreset);
								onChangePreset(newPreset);
							}}
							onCheckIfNameIsAvailable={bulkAnalysisRepository.isPresetNameAvailable}
							onDeletePreset={async (preset) => {
								await bulkAnalysisRepository.deletePreset(preset.uuid!);
								await rq.refetch();
							}}
							onRenamePreset={async (name, preset) => {
								await bulkAnalysisRepository.updatePreset({
									...preset,
									filters: preset.filters ? convertFilterResponseToFilter(preset.filters) : undefined,
									name,
								});
								await rq.refetch();
							}}
							onToggleFavoritePreset={async (isFavorite, preset) => {
								if (isFavorite && preset?.uuid) {
									await bulkAnalysisRepository.setFavoritePreset(preset.uuid);
									await rq.refetch();
								}
							}}
						/>
					)}
				</ReactQueryWrapperBase>
			}
			footer={
				<div className="flex justify-between">
					<Button
						palette="tertiary"
						size="small"
						onClick={() => {
							onChangePreset(emptyInsightPanelPreset);
							setInsightPanelPreset(emptyInsightPanelPreset);
						}}
					>
						{t("BUTTON.CLEAR_ALL")}
					</Button>
					{insightPanelPreset.standard || !insightPanelPreset.uuid ? (
						<div className="flex gap-2">
							<Button
								onClickAsync={() =>
									spawnCreateDialog({
										entityType: "preset",
										onSubmitAsync: (name) => onSaveAsNewPreset(lastInsightPanelPresetRef.current, name),
										checkIfNameIsAvailable: bulkAnalysisRepository.isPresetNameAvailable,
										placeholder: "insert a name",
									}).then(() => queryPresets.refetch())
								}
								palette="primary"
								size="small"
							>
								{t("BUTTON.SAVE_AS_NEW")}
							</Button>
						</div>
					) : (
						<div className="flex gap-2">
							<Button
								onClickAsync={() =>
									spawnCreateDialog({
										entityType: "preset",
										onSubmitAsync: (name) => onSaveAsNewPreset(lastInsightPanelPresetRef.current, name),
										checkIfNameIsAvailable: bulkAnalysisRepository.isPresetNameAvailable,
										placeholder: "insert a name",
									}).then(() => queryPresets.refetch())
								}
								palette="secondary"
								size="small"
							>
								{t("BUTTON.SAVE_AS_NEW")}
							</Button>
							<Button
								onClickAsync={async () => {
									await runWithErrorReporting(
										async () => {
											try {
												const {
													search: _search,
													metric,
													filters,
													favorite,
													...params
												} = lastInsightPanelPresetRef.current;
												const { keyMetricThreshold, ...reset } = metric ?? {};
												const keyMetricThresholdValues = keyMetricThreshold?.first();
												await bulkAnalysisRepository.updatePreset({
													...params,
													favourite: favorite,
													filters: convertFilterResponseToFilter(Array.from(filters?.values() ?? [])),
													metric: {
														...reset,
														thresholdEntity: keyMetricThresholdValues?.thresholdEntity,
														thresholdType: keyMetricThresholdValues?.thresholdType,
														thresholdValue: keyMetricThresholdValues?.thresholdValue,
													},
												});
											} catch (error) {
												throw new ToastableError("Something went wrong please try later", {
													icon: "Portfolio",
													cause: error,
												});
											}
										},
										{ area: "portfolio", attemptedOperation: { message: "try to save preset" } },
									);

									await queryPresets.refetch();
								}}
								palette="primary"
								size="small"
							>
								{t("BUTTON.SAVE")}
							</Button>
						</div>
					)}
				</div>
			}
		>
			<div className="relative z-0">
				<Separator text="Filters" align="start" className="mb-2" />
				<DebouncedSearchInput
					externalQuery={insightPanelPreset?.search ?? ""}
					onChange={(search) => {
						trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.filters.search", null);
						setInsightPanelPreset((preset) => ({ ...preset, search }));
						onChangePreset(lastInsightPanelPresetRef.current);
					}}
					placeholder="search portfolio by name"
					data-qualifier="InsightPanel/SearchByKeyWord"
					className="mb-2"
				/>
				<ReactQueryWrapperBase query={queryCommonSelectableFilters}>
					{(selectable) => (
						<>
							<PortfolioInsightPanelInner
								selectable={selectable}
								preset={insightPanelPreset}
								onAddFilter={() => {
									trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.filters.change", null);
									setInsightPanelPreset((preset) => ({
										...preset,
										filters: OrderedMap(handleMapTruthy(preset?.filters).set(generateUniqueDOMId(), {})),
									}));
								}}
								onChangeFilter={(id, payload) => {
									trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.filters.change", null);
									setInsightPanelPreset((preset) => ({
										...preset,
										filters: OrderedMap(handleMapTruthy(preset?.filters).set(id, payload)),
									}));
								}}
								onRemoveFilter={(id) => {
									trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.filters.change", null);
									setInsightPanelPreset((preset) => ({
										...preset,
										filters: OrderedMap(handleMapTruthy(preset?.filters).remove(id)),
									}));
								}}
								onCommitFilters={() => onChangePreset(lastInsightPanelPresetRef.current)}
							/>
							<Separator text="Sorting parameters" align="start" className="mb-2" />
							<ReactQueriesWrapperBase queries={queriesCustomKeyMetrics}>
								{([selectableEntities, { assetClassCategories }]) => (
									<PortfolioInsightPanelMetricInner
										key={metricBlockHash}
										selectableEntities={selectableEntities}
										userClassifications={userClassifications}
										assetClassCategories={assetClassCategories ?? []}
										selectable={selectable}
										preset={insightPanelPreset}
										onChangeMetric={(newMetric) => {
											trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.keyMetric.change", {
												metric: {
													key: newMetric.key,
													metricParameters: newMetric.metricParameters,
												},
											});
											setInsightPanelPreset((preset) => ({
												...preset,
												metric: {
													...preset?.metric,
													key: newMetric.key,
													metricParameters: newMetric.metricParameters,
												},
											}));
										}}
										onChangeReferenceType={(newReferenceType) => {
											setInsightPanelPreset((preset) => ({
												...preset,
												metric: {
													...preset?.metric,
													referenceType: newReferenceType,
												},
											}));
										}}
										onChangeReferenceEntity={(newReferenceEntity) => {
											trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.keyMetric.reference", null);
											setInsightPanelPreset((preset) => ({
												...preset,
												metric: {
													...preset?.metric,
													referenceEntity: newReferenceEntity,
												},
											}));
										}}
										onAddKeyMetricThreshold={() => {
											setInsightPanelPreset((preset) => ({
												...preset,
												metric: {
													...preset?.metric,
													keyMetricThreshold: handleMapTruthy(preset?.metric?.keyMetricThreshold).set(
														generateUniqueDOMId(),
														{},
													),
												},
											}));
										}}
										onChangeKeyMetricThreshold={(id, payload) => {
											setInsightPanelPreset((preset) => ({
												...preset,
												metric: {
													...preset?.metric,
													keyMetricThreshold: handleMapTruthy(preset?.metric?.keyMetricThreshold).set(id, payload),
												},
											}));
										}}
										onRemoveKeyMetricThreshold={(id) => {
											setInsightPanelPreset((preset) => ({
												...preset,
												metric: {
													...preset?.metric,
													keyMetricThreshold: handleMapTruthy(preset?.metric?.keyMetricThreshold).remove(id),
												},
											}));
										}}
										onChangeOrder={(newOrdering) => {
											setInsightPanelPreset((preset) => ({
												...preset,
												metric: {
													...preset?.metric,
													ordering: newOrdering,
												},
											}));
										}}
										onCommitKeyParameter={() => onChangePreset(lastInsightPanelPresetRef.current)}
										onCommitKeyThreshold={() => onChangePreset(lastInsightPanelPresetRef.current)}
									/>
								)}
							</ReactQueriesWrapperBase>
						</>
					)}
				</ReactQueryWrapperBase>
			</div>
		</SideDrawer>
	);
}

type PortfolioInsightPanelInnerProps = {
	selectable: AvailableFieldsResponse;
	preset?: PortfolioInsightPanelPreset;

	onChangeFilter(id: string, payload: FilterResponse): void;
	onRemoveFilter(id: string): void;
	onAddFilter(): void;

	onCommitFilters(): void;
};

const keyMetricSelectionEntities: Array<SelectOption<MetricThresholdType>> = [
	{
		label: "Manual",
		value: MetricThresholdType.Manual,
	},
	{
		label: "Portfolio",
		value: MetricThresholdType.Investment,
	},
	{
		label: "Reference",
		value: MetricThresholdType.TargetInvestment,
	},
	// {
	// 	label: "Standard benchmark",
	// 	value: MetricThresholdType.StandardBenchmark,
	// },
	// {
	// 	label: "Legacy benchmark",
	// 	value: MetricThresholdType.CustomBenchmark,
	// },
];

const allowedSelectionByKey = {
	CURRENT_ACTION: 0,
	BENCHMARK_NAME: 0,
	COMMENTARY_TEMPLATE: 0,
	CURRENCY_NAME: 0,
	REFERENCE_NAME: 0,
	UNIVERSE_NAME: 0,
	AVERAGE_SCORE: 0,
	STATUS: 0,
	NAME: 0,
	EFFICIENCY_RATIO_1M: Infinity,
	MAX_DRAWDOWN_1M: Infinity,
	SORTINO_1M: Infinity,
	VOLATILITY_1M: Infinity,
	INCEPTION_DATE: Infinity,
	LAST_STATUS_UPDATE: Infinity,
	PERFORMANCE_1M: Infinity,
	AUTO_SYNC: Infinity,
	MARKET_VIEW_NAME: Infinity,
};

function PortfolioInsightPanelInner({
	selectable,
	preset,
	onAddFilter,
	onChangeFilter,
	onCommitFilters,
	onRemoveFilter,
}: PortfolioInsightPanelInnerProps): React.JSX.Element {
	const { filters } = preset ?? {};
	const { t } = useTranslation();

	const availableFiltersMap = useMemo<Map<string, FilterValues>>(() => {
		if (!selectable.availableFilters) {
			return Map();
		}

		return Map(selectable.availableFilters.flatMap((x) => (x.key ? [[x.key, x]] : [])));
	}, [selectable.availableFilters]);

	const aggregateFilters = useMemo(
		() =>
			groupBy(
				Array.from(filters?.values() ?? []).filter(({ key }) => key),
				(x) => x.key!,
			),
		[filters],
	);

	const availableFilterKeys = useMemo<Array<SelectOption<FieldKey>>>(() => {
		if (!selectable.availableFilters) {
			return [];
		}
		return selectable.availableFilters.flatMap((x) =>
			x.key
				? [
						{
							label: t(`METRIC_COMPARISON_CHART.${x.key}`),
							value: x.key,
							disabled:
								allowedSelectionByKey[x.key as keyof typeof allowedSelectionByKey] <
								(aggregateFilters[x.key]?.length ?? 0),
						},
					]
				: [],
		);
	}, [selectable.availableFilters, t, aggregateFilters]);

	return (
		<>
			<div className="mb-2">
				<ForEach collection={preset?.filters?.toArray() ?? []} keyProvider={(item) => item[0]}>
					{([id, opt], index) => (
						<Transition
							in
							initialIn={false}
							className="transition-[opacity,transform,scale] origin-top"
							enterFromClassName="opacity-0 scale-95"
							enterToClassName="opacity-100 scale-100"
						>
							{({ className }) => (
								<div className={overrideClassName(className, "flex items-center")}>
									<div className="flex gap-2 flex-1 max-w-[calc(100%-38px)]">
										<Select
											triggerDataAttrs={{
												"data-qualifier": `InsightPanel/Filter(${index})/Trigger`,
											}}
											data-qualifier={`InsightPanel/Filter(${index})/Dropdown`}
											options={availableFilterKeys}
											value={opt.key ?? null}
											enableSearch
											onChange={(newKey) => {
												if (newKey) {
													onChangeFilter(id, {
														key: newKey,
														type: availableFiltersMap.get(newKey)!.type!,
														relation: undefined,
														value: undefined,
													});
												}
												onCommitFilters();
											}}
											className="w-[139px]"
										/>

										{opt.key &&
											opt.type &&
											match(opt.type)
												.with(
													"FINITE_SET_PORTFOLIOS",
													"FINITE_SET_STATUSES",
													"FINITE_SET_ACTIONS",
													"FINITE_SET_BASE_CURRENCIES",
													"FINITE_SET_UNIVERSES",
													"FINITE_SET_BENCHMARKS",
													"FINITE_SET_REFERENCES",
													"FINITE_SET_MARKET_VIEWS",
													"FINITE_SET_COMMENTARY_TEMPLATES",
													() => {
														return (
															<ReactQueryWrapper
																queryKey={["queryFieldSetKey", opt.key, opt.type]}
																queryFn={async () => {
																	const { fieldSet } = await bulkAnalysisRepository.fieldSet.fetchQuery(opt.type!);
																	const options: SelectOption<string>[] =
																		fieldSet
																			?.filter((x) => x.label && x.identifier)
																			.map((x) => ({
																				label: x.label!,
																				value: x.identifier!,
																			})) ?? []; // it was identifier before, change in MDOT-18036
																	return { options };
																}}
																cacheTime={minutes(10)}
																loadingFallback={
																	<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />
																}
															>
																{({ options }) => (
																	<Select
																		triggerDataAttrs={{
																			"data-qualifier": `InsightPanel/Filter(${index})/${opt.key}/Trigger`,
																		}}
																		multi
																		enableSearch
																		data-qualifier={`InsightPanel/Filter(${index})/${opt.key}/Dropdown`}
																		options={options}
																		value={opt.value ?? []}
																		onChange={(identifiers) => {
																			if (identifiers) {
																				onChangeFilter(id, {
																					...opt,
																					value: identifiers,
																					relation: "EQUAL",
																				});
																				onCommitFilters();
																			}
																		}}
																		className="w-[139px]"
																	/>
																)}
															</ReactQueryWrapper>
														);
													},
												)
												.with("BOOLEAN", () => (
													<Select
														triggerDataAttrs={{
															"data-qualifier": `InsightPanel/Filter(${index})/${opt.key}/Trigger`,
														}}
														data-qualifier={`InsightPanel/Filter(${index})/${opt.key}/Dropdown`}
														options={[
															{ label: "Yes", value: true },
															{ label: "No", value: false },
														]}
														value={opt.value ? opt.value[0] === "true" : undefined}
														onChange={(flag) => {
															onChangeFilter(id, { ...opt, value: [String(flag)], relation: "EQUAL" });
															onCommitFilters();
														}}
														className="w-[139px]"
													/>
												))
												.with("DATE", "NUMERIC", "PERCENTAGE", (filterType) => {
													const options =
														availableFiltersMap.get(opt.key!)?.availableRelations?.flatMap((relation) =>
															relation
																? [
																		{
																			label:
																				filterType === "DATE"
																					? t(`RELATION.CALENDAR.${relation}`)
																					: t(`RELATION.${relation}`),
																			value: relation,
																		},
																	]
																: [],
														) ?? [];
													return (
														<Select
															triggerDataAttrs={{
																"data-qualifier": `InsightPanel/Filter(${index})/${opt.key}/Trigger`,
															}}
															data-qualifier={`InsightPanel/Filter(${index})/${opt.key}/Dropdown`}
															options={options}
															value={opt.relation}
															onChange={(relation) => {
																if (relation) {
																	onChangeFilter(id, { ...opt, relation });
																}

																if (relation && opt.value) {
																	onCommitFilters();
																}
															}}
															className="w-[139px]"
														/>
													);
												})
												.exhaustive()}

										{opt.key &&
											opt.relation &&
											opt.type &&
											match(opt.type)
												.with("PERCENTAGE", () => (
													<NumberInput
														data-qualifier={`InsightPanel/Filter(${index})/${opt.key}/Percentage`}
														value={opt.value?.[0] ? Number(opt.value?.[0]) : null}
														rightContent={<Icon icon="Percentile" />}
														placeholder="0"
														onChange={(value) =>
															onChangeFilter(id, { ...opt, value: value != null ? [String(value)] : undefined })
														}
														className="w-24"
														onBlur={() => onCommitFilters()}
													/>
												))
												.with("NUMERIC", () => (
													<NumberInput
														data-qualifier={`InsightPanel/Filter(${index})/${opt.key}/NumericValue`}
														value={opt.value?.[0] ? Number(opt.value?.[0]) : null}
														placeholder="0"
														onChange={(value) =>
															onChangeFilter(id, { ...opt, value: value != null ? [String(value)] : undefined })
														}
														className="w-24"
														onBlur={() => onCommitFilters()}
													/>
												))
												.with("DATE", () => (
													<DatePickerInput
														data-qualifier={`InsightPanel/Filter(${index})/${opt.key}/DatePicker`}
														value={opt.value?.[0] ?? ""}
														className="w-24"
														onChange={(newDate) => {
															onChangeFilter(id, {
																...opt,
																value: newDate ? [new Date(newDate).toISOString()] : undefined,
															});
															onCommitFilters();
														}}
													/>
												))
												.otherwise(() => <></>)}
									</div>
									<IconButton
										data-qualifier={`InsightPanel/Filter(${index})/${opt.key}/Delete`}
										icon="Delete"
										onClick={() => {
											onRemoveFilter(id);
											onCommitFilters();
										}}
										size={18}
										color={themeCSSVars.palette_P400}
										className="ml-auto "
									/>
								</div>
							)}
						</Transition>
					)}
				</ForEach>
			</div>
			<Button palette="secondary" onClick={() => onAddFilter()} size="small" data-qualifier="InsightPanel/AddFilter">
				<Icon icon="Outline1" />
			</Button>
		</>
	);
}

const keyMetricsToRemove: FieldKey[] = [
	"SCORE_AVERAGE_NAN_AS_ZEROS",
	"SCORE_AVERAGE",
	"TAG_EXPOSURE_OPTION",
	"RISK_MODEL_EXPOSURE_AC",
	"EXPOSURE_MACRO_EQUITY",
	"EXPOSURE_MACRO_FIXED_INCOME",
	"EXPOSURE_MACRO_COMMODITIES",
	"EXPOSURE_MACRO_ALTERNATIVE",
	"EXPOSURE_MACRO_MONEY_MARKET",
];

function mixAssetClassCategories(
	assetClass: RiskModelExposureType[],
	assetClassMix: Array<RiskModelExposureType[]>,
	metric: RichMetricValues[],
) {
	assetClass.forEach((assetClassCategory) => {
		assetClassCategory.assetClasses?.forEach((assetClassOption) => {
			for (const mix of assetClassMix) {
				mix.forEach((mixOption) => {
					mixOption.assetClasses?.forEach((option) => {
						const riskModelTags = {
							key: "RISK_MODEL_EXPOSURE_AC",
							type: "PERCENTAGE",
							riskModelExposureAssetClass: assetClassOption,
							riskModelExposureType: assetClassCategory.identifier,
							riskModelSecondLevelExposureAssetClass: option,
							riskModelSecondLevelExposureType: mixOption.identifier,
							taxonomy: [
								{
									entry: "EXPOSURES",
								},
								{
									parent: "EXPOSURES",
									entry: "RISK_MODEL",
								},
								{
									parent: "RISK_MODEL",
									entry: assetClassCategory.name!,
								},
								{
									parent: assetClassCategory.name!,
									entry: assetClassOption,
								},
								{
									parent: assetClassOption,
									entry: mixOption.name!,
								},
							],
						} satisfies RichMetricValues;
						metric.push(riskModelTags);
					});
				});
			}
		});
	});
}

function addAssetClassCategories(assetClass: RiskModelExposureType[], metric: RichMetricValues[]) {
	assetClass?.forEach((assetClassCategory) => {
		assetClassCategory.assetClasses?.forEach((assetClass) => {
			const riskModelTags = {
				key: "RISK_MODEL_EXPOSURE_AC",
				type: "PERCENTAGE",
				riskModelExposureAssetClass: assetClass,
				riskModelExposureType: assetClassCategory.identifier,
				taxonomy: [
					{
						entry: "EXPOSURES",
					},
					{
						parent: "EXPOSURES",
						entry: "RISK_MODEL",
					},
					{
						parent: "RISK_MODEL",
						entry: assetClassCategory.name ?? "Missing tag category",
					},
				],
			} satisfies RichMetricValues;
			metric.push(riskModelTags);
		});
	});
}
const maxDepth = 2;
function PortfolioInsightPanelMetricInner({
	selectable,
	selectableEntities,
	userClassifications,
	assetClassCategories,
	preset,
	onCommitKeyParameter,
	onCommitKeyThreshold,
	onChangeMetric,
	onChangeReferenceType,
	onChangeReferenceEntity,
	onChangeOrder,
	onChangeKeyMetricThreshold,
	onRemoveKeyMetricThreshold,
	onAddKeyMetricThreshold,
}: {
	selectable: Omit<AvailableFieldsResponse, "availableEntities">;
	selectableEntities: AvailableEntitiesResponse;
	userClassifications: UserInstrumentClassificationDto[];
	assetClassCategories: RiskModelExposureType[];
	preset?: PortfolioInsightPanelPreset;

	onCommitKeyParameter(): void;
	onCommitKeyThreshold(): void;

	onChangeMetric(newKeyMetric: Extract<HierarchicalMetrics, { type: "leaf" }>): void;
	onChangeReferenceType(newReferenceType: MetricReferenceType): void;
	onChangeReferenceEntity(referenceEntity?: ReferenceEntityValues): void;

	onChangeOrder(newOrdering?: MetricOrdering): void;

	onChangeKeyMetricThreshold(id: string, payload: keyMetricThresholdMapType): void;
	onRemoveKeyMetricThreshold(id: string): void;
	onAddKeyMetricThreshold(): void;
}) {
	const { metric } = preset ?? {};
	const { t } = useTranslation();

	const enrichedMetrics = useMemo(() => {
		if (!selectable.availableMetrics) {
			return [];
		}
		const apiMetricsResponse = selectable.availableMetrics.filter((x) =>
			x.key ? !keyMetricsToRemove.includes(x.key) : false,
		) as RichMetricValues[]; //force casting

		const groupedUserClassifications = groupBy(userClassifications, (x) => x.fieldType!);

		groupedUserClassifications.SCORE?.forEach((x) => {
			const scoreClassifications = {
				key: "SCORE_AVERAGE",
				type: "NUMERIC",
				classification: {
					classificationUuid: x.classificationUuid!,
					name: x.name!,
				},
				taxonomy: [
					{
						entry: "CUSTOM_SCORES_AND_METRICS",
					},
				],
			} satisfies RichMetricValues;
			apiMetricsResponse.push(scoreClassifications);
		});

		groupedUserClassifications.TAG?.forEach((classification) => {
			classification.options?.forEach((opt) => {
				const tagClassifications = {
					key: "TAG_EXPOSURE_OPTION", // change
					type: "PERCENTAGE",
					classification: {
						classificationUuid: classification.classificationUuid!,
						name: classification.name!,
					},
					option: opt.option,
					taxonomy: [
						{
							entry: "EXPOSURES",
						},
						{
							parent: "EXPOSURES",
							entry: "TAG_EXPOSURE_OPTION",
						},
						{
							parent: "TAG_EXPOSURE_OPTION",
							entry: classification.name!,
						},
					],
				} satisfies RichMetricValues;
				apiMetricsResponse.push(tagClassifications);
			});
		});

		//MACRO_GEOGRAPHY, CURRENCY, MICRO_ASSET_CLASS, MACRO_ASSET_CLASS, MICRO_GEOGRAPHY
		const groupedAssetClasses = groupBy(assetClassCategories, (x) => x.identifier!);

		mixAssetClassCategories(
			groupedAssetClasses["MACRO_ASSET_CLASS"] ?? [],
			[
				groupedAssetClasses["MICRO_ASSET_CLASS"] ?? [],
				groupedAssetClasses["MACRO_GEOGRAPHY"] ?? [],
				groupedAssetClasses["MICRO_GEOGRAPHY"] ?? [],
			],
			apiMetricsResponse,
		);

		mixAssetClassCategories(
			groupedAssetClasses["MICRO_ASSET_CLASS"] ?? [],
			[groupedAssetClasses["MACRO_GEOGRAPHY"] ?? [], groupedAssetClasses["MICRO_GEOGRAPHY"] ?? []],
			apiMetricsResponse,
		);

		mixAssetClassCategories(
			groupedAssetClasses["MACRO_GEOGRAPHY"] ?? [],
			[
				groupedAssetClasses["MACRO_ASSET_CLASS"] ?? [],
				groupedAssetClasses["MICRO_ASSET_CLASS"] ?? [],
				groupedAssetClasses["MICRO_GEOGRAPHY"] ?? [],
			],
			apiMetricsResponse,
		);

		mixAssetClassCategories(
			groupedAssetClasses["MICRO_GEOGRAPHY"] ?? [],
			[groupedAssetClasses["MACRO_ASSET_CLASS"] ?? [], groupedAssetClasses["MICRO_ASSET_CLASS"] ?? []],
			apiMetricsResponse,
		);

		addAssetClassCategories(groupedAssetClasses["MACRO_ASSET_CLASS"] ?? [], apiMetricsResponse);
		addAssetClassCategories(groupedAssetClasses["MICRO_ASSET_CLASS"] ?? [], apiMetricsResponse);
		addAssetClassCategories(groupedAssetClasses["MACRO_GEOGRAPHY"] ?? [], apiMetricsResponse);
		addAssetClassCategories(groupedAssetClasses["MICRO_GEOGRAPHY"] ?? [], apiMetricsResponse);
		addAssetClassCategories(groupedAssetClasses["CURRENCY"] ?? [], apiMetricsResponse);

		return apiMetricsResponse;
	}, [userClassifications, selectable.availableMetrics, assetClassCategories]);

	const availableHierarchicalKeys = useMemo(() => createTreeHierarchicalOption(enrichedMetrics), [enrichedMetrics]);

	const [hierarchicalMetrics, setHierarchicalMetrics] = useState<HierarchicalMetrics[]>(
		metric?.key
			? chunk(
					createLeafPathFromValue(availableHierarchicalKeys, {
						value: {
							key: metric.key,
							type: "leaf",
							...(metric?.metricParameters ? { metricParameters: metric?.metricParameters } : {}),
						},
					}) ?? [],
					maxDepth,
				)?.reduce((acc, tuple) => [...acc, tuple.at(-1)!], [])
			: [],
	);

	const availableEntitiesMap = useMemo<Map<string, ReferenceEntityValues>>(() => {
		if (!selectableEntities.availableEntities) {
			return Map();
		}
		return Map(selectableEntities.availableEntities.flatMap((x) => (x.identifier ? [[x.identifier, x]] : [])));
	}, [selectableEntities.availableEntities]);

	// Entities grouped by metric-entity type, in first-seen order, each carrying its translated
	// heading. Kept as `[id, label, items]` so consumers can both select groups by id and emit the
	// `[heading, items]` tuples the modern `<Select/>` expects.
	const availableEntityGroups = useMemo(() => {
		const groups: Array<[id: MetricEntityType, label: string, items: SelectOption<string>[]]> = [];
		const indexById: Partial<Record<MetricEntityType, number>> = {};
		for (const x of selectableEntities.availableEntities ?? []) {
			if (!x.label || !x.identifier || !x.type) {
				continue;
			}
			const id = (
				x.type === "TARGET_PORTFOLIO" && x.labelledAsBenchmark ? "USER_BENCHMARK" : x.type
			) as MetricEntityType;
			let idx = indexById[id];
			if (idx === undefined) {
				idx = groups.length;
				indexById[id] = idx;
				groups.push([id, t(`METRIC_SELECTION.${id}`), []]);
			}
			groups[idx][2].push({ label: x.label, value: x.identifier });
		}
		return groups;
	}, [selectableEntities.availableEntities, t]);

	// Reference select: the SELF group first, then every other group.
	const referenceEntityOptions = useMemo<SelectOptions<string>>(
		() =>
			[
				...availableEntityGroups.filter(([id]) => id === "SELF"),
				...availableEntityGroups.filter(([id]) => id !== "SELF"),
			].map(([, label, items]) => [label, items] as [string, SelectOption<string>[]]),
		[availableEntityGroups],
	);

	const availableEntitiesByGroup = useMemo<SelectOptions<string>>(() => {
		const selectedThresholdType = metric?.keyMetricThreshold?.first()?.thresholdType;
		const pick = (...ids: MetricEntityType[]): SelectOptions<string> =>
			ids
				.flatMap((id) => availableEntityGroups.filter(([gid]) => gid === id))
				.map(([, label, items]) => [label, items] as [string, SelectOption<string>[]]);

		return (
			match(selectedThresholdType)
				.with("MANUAL", undefined, (): SelectOptions<string> => [])
				// .with("SELF", () => pick("SELF")) // TODO: add when SELF api is ready on BE
				.with("INVESTMENT", () => pick("INVESTMENT"))
				.with("TARGET_INVESTMENT", "CUSTOM_BENCHMARK", () => pick("TARGET_PORTFOLIO", "USER_BENCHMARK"))
				.with("STANDARD_BENCHMARK", () => pick("STANDARD_BENCHMARK"))
				.exhaustive() ?? []
		);
	}, [availableEntityGroups, metric?.keyMetricThreshold]);
	const tick = useTick();
	return (
		<>
			<Controller value={false}>
				{({ value: isLoading, onChange: setIsLoading }) => (
					<FormField label="Key Metric" className="mb-2">
						{(filedProps) => (
							<div className="grid grid-cols-2 gap-2">
								<MultipleHierarchicalSelect
									{...filedProps}
									options={availableHierarchicalKeys}
									value={hierarchicalMetrics}
									maxDepth={maxDepth}
									disabled={isLoading}
									onChange={(newHierarchicalMetrics) => {
										setHierarchicalMetrics(newHierarchicalMetrics);
										const leafs = newHierarchicalMetrics.filter((x) => x.type === "leaf");

										if (leafs.length === 0) {
											return;
										}

										const leaf = leafs.at(-1);

										if (leaf) {
											const flatHierarchicalMetrics: Extract<HierarchicalMetrics, { type: "leaf" }> = {
												type: "leaf",
												key: leaf.key,
												metricParameters: leaf.metricParameters,
											};

											onChangeMetric(flatHierarchicalMetrics);

											if (
												flatHierarchicalMetrics.key &&
												((metric?.referenceType === "ABSOLUTE" && metric.ordering) ||
													(metric?.referenceType === "DELTA_RELATIVE" && metric?.referenceEntity?.identifier))
											) {
												unpromisify(() => onCommitKeyParameter())();
											}

											const keyMetricThreshold = metric?.keyMetricThreshold?.first();
											const keyMetricThresholdKey = metric?.keyMetricThreshold?.keySeq().first();
											if (
												keyMetricThreshold?.thresholdType &&
												keyMetricThreshold?.thresholdType !== "MANUAL" &&
												keyMetricThreshold.thresholdEntity?.identifier &&
												keyMetricThresholdKey &&
												flatHierarchicalMetrics
											) {
												(async () => {
													setIsLoading(true);
													try {
														const { thresholdValue } =
															await bulkAnalysisRepository.bulkAnalysisThresholdForMetric.fetchQuery({
																metricKey: flatHierarchicalMetrics.key,
																metricParameters: flatHierarchicalMetrics.metricParameters,
																metricThresholdEntity: metric?.keyMetricThreshold?.first()?.thresholdEntity,
																metricReferenceEntity: metric?.referenceEntity,
																metricReferenceType: metric?.referenceType,
															});

														if (thresholdValue) {
															onChangeKeyMetricThreshold(keyMetricThresholdKey, {
																...keyMetricThreshold,
																thresholdValue,
															});
														}

														if (metric?.key) {
															onCommitKeyThreshold();
														}
													} catch (err) {
														console.error(err);
														platformToast({
															children: "Unable to compute threshold value, please re-try",
															icon: "Icon-full-error",
															severity: "error",
														});
													} finally {
														setIsLoading(false);
													}
												})().catch(noop);
											}
										}
									}}
									hierarchicalSelectClassName={(depth) => (depth === 0 ? "col-span-2" : "")}
								/>
							</div>
						)}
					</FormField>
				)}
			</Controller>

			<div className="flex items-end gap-4 mb-2">
				<Controller value={false}>
					{({ value: isLoading, onChange: setIsLoading }) => (
						<FormField label="Value type" className="w-[115px] shrink-0">
							{(filedProps) => (
								<Select
									{...filedProps}
									triggerDataAttrs={{
										"data-qualifier": "InsightPanel/SortingParameters/ValueType/Trigger",
									}}
									data-qualifier="InsightPanel/SortingParameters/ValueType/Dropdown"
									options={[
										{ label: "Absolute", value: MetricReferenceType.Absolute },
										{ label: "Relative", value: MetricReferenceType.DeltaRelative },
									]}
									disabled={isLoading}
									value={metric?.referenceType}
									onChange={(newReferenceType) => {
										if (newReferenceType) {
											onChangeReferenceType(newReferenceType);
										}

										if (newReferenceType === "ABSOLUTE") {
											tick()
												.then(() => onChangeReferenceEntity())
												.catch(console.warn);
										}

										if (
											metric?.key &&
											((newReferenceType === "ABSOLUTE" && metric.ordering) ||
												(newReferenceType === "DELTA_RELATIVE" && metric?.referenceEntity?.identifier))
										) {
											unpromisify(() => onCommitKeyParameter())();
										}

										const keyMetricThreshold = metric?.keyMetricThreshold?.first();
										const keyMetricThresholdKey = metric?.keyMetricThreshold?.keySeq().first();

										if (
											keyMetricThreshold?.thresholdType &&
											keyMetricThreshold?.thresholdType !== "MANUAL" &&
											keyMetricThreshold.thresholdEntity?.identifier &&
											keyMetricThresholdKey &&
											metric?.key
										) {
											(async () => {
												setIsLoading(true);
												try {
													let thresholdValue: number | undefined;
													if (newReferenceType === "ABSOLUTE") {
														({ thresholdValue } =
															await bulkAnalysisRepository.bulkAnalysisThresholdForMetric.fetchQuery({
																metricKey: metric?.key,
																metricThresholdEntity: metric?.keyMetricThreshold?.first()?.thresholdEntity,
																metricReferenceType: newReferenceType,
															}));
													}

													if (newReferenceType === "DELTA_RELATIVE" && metric?.referenceEntity?.identifier) {
														({ thresholdValue } =
															await bulkAnalysisRepository.bulkAnalysisThresholdForMetric.fetchQuery({
																metricKey: metric?.key,
																metricThresholdEntity: metric?.keyMetricThreshold?.first()?.thresholdEntity,
																metricReferenceType: newReferenceType,
																metricReferenceEntity: metric?.referenceEntity,
															}));
													}

													if (thresholdValue != null) {
														onChangeKeyMetricThreshold(keyMetricThresholdKey, {
															...keyMetricThreshold,
															thresholdValue,
														});
													}

													if (metric?.key) {
														onCommitKeyThreshold();
													}
												} catch (err) {
													console.error(err);
													platformToast({
														children: "Unable to compute threshold value, please re-try",
														icon: "Icon-full-error",
														severity: "error",
													});
												} finally {
													setIsLoading(false);
												}
											})().catch(noop);
										}
									}}
								/>
							)}
						</FormField>
					)}
				</Controller>
				<Controller value={false}>
					{({ value: isLoading, onChange: setIsLoading }) => (
						<FormField label="" className="min-w-0 grow">
							{(filedProps) => (
								<Select
									{...filedProps}
									triggerDataAttrs={{
										"data-qualifier": "InsightPanel/SortingParameters/Reference/Trigger",
									}}
									data-qualifier="InsightPanel/SortingParameters/Reference/Dropdown"
									options={referenceEntityOptions}
									enableSearch
									value={metric?.referenceEntity?.identifier ?? null}
									disabled={metric?.referenceType === "ABSOLUTE" || metric?.referenceType === undefined}
									className="w-full"
									onChange={(entityIdentifier) => {
										if (entityIdentifier) {
											onChangeReferenceEntity(availableEntitiesMap.get(entityIdentifier));
										}

										if (
											metric?.key &&
											((metric?.referenceType === "ABSOLUTE" && metric.ordering) ||
												(metric?.referenceType === "DELTA_RELATIVE" && entityIdentifier))
										) {
											unpromisify(() => onCommitKeyParameter())();
										}

										const keyMetricThreshold = metric?.keyMetricThreshold?.first();
										const keyMetricThresholdKey = metric?.keyMetricThreshold?.keySeq().first();

										if (
											keyMetricThreshold?.thresholdType &&
											keyMetricThreshold?.thresholdType !== "MANUAL" &&
											keyMetricThreshold.thresholdEntity?.identifier &&
											keyMetricThresholdKey &&
											metric?.key
										) {
											(async () => {
												setIsLoading(true);
												try {
													let thresholdValue: number | undefined;
													if (metric?.referenceType === "ABSOLUTE") {
														({ thresholdValue } =
															await bulkAnalysisRepository.bulkAnalysisThresholdForMetric.fetchQuery({
																metricKey: metric?.key,
																metricThresholdEntity: metric?.keyMetricThreshold?.first()?.thresholdEntity,
																metricReferenceType: metric?.referenceType,
															}));
													}

													if (metric?.referenceType === "DELTA_RELATIVE" && entityIdentifier) {
														({ thresholdValue } =
															await bulkAnalysisRepository.bulkAnalysisThresholdForMetric.fetchQuery({
																metricKey: metric?.key,
																metricThresholdEntity: metric?.keyMetricThreshold?.first()?.thresholdEntity,
																metricReferenceType: metric?.referenceType,
																metricReferenceEntity: availableEntitiesMap.get(entityIdentifier),
															}));
													}

													if (thresholdValue != null) {
														onChangeKeyMetricThreshold(keyMetricThresholdKey, {
															...keyMetricThreshold,
															thresholdValue,
														});
													}

													if (metric?.key) {
														onCommitKeyThreshold();
													}
												} catch (err) {
													console.error(err);
													platformToast({
														children: "Unable to compute threshold value, please re-try",
														icon: "Icon-full-error",
														severity: "error",
													});
												} finally {
													setIsLoading(false);
												}
											})().catch(noop);
										}
									}}
								/>
							)}
						</FormField>
					)}
				</Controller>
			</div>
			<FormField label="Order" className="mb-2">
				{() => {
					function changeOrder(newOrdering: MetricOrdering | undefined) {
						onChangeOrder(newOrdering);
						if (
							metric?.key &&
							((metric?.referenceType === "ABSOLUTE" && newOrdering) ||
								(metric?.referenceType === "DELTA_RELATIVE" && metric.referenceEntity?.identifier))
						) {
							unpromisify(() => onCommitKeyParameter())();
						}
					}
					return (
						<AutoTooltip
							maxWidth={200}
							position="top"
							overrideColor={themeCSSVars.palette_N200}
							trigger={({ innerRef }) => (
								<ButtonGroup
									buttons={[
										{
											"data-qualifier": `InsightPanel/SortingParameters/Order/${MetricOrdering.Asc}`,
											onClick: () => changeOrder(MetricOrdering.Asc),
											selected: metric?.ordering === MetricOrdering.Asc,
											icon: "sort-ascending",
											children: "Asc",
										},
										{
											"data-qualifier": `InsightPanel/SortingParameters/Order/${MetricOrdering.Desc}`,
											onClick: () => changeOrder(MetricOrdering.Desc),
											selected: metric?.ordering === MetricOrdering.Desc,
											icon: "sort-decending",
											children: "Desc",
										},
										{
											"data-qualifier": `InsightPanel/SortingParameters/Order/${MetricOrdering.Unsorted}`,
											disabled: metric?.ordering !== MetricOrdering.Unsorted,
											onClick: () => changeOrder(MetricOrdering.Unsorted),
											selected: metric?.ordering === MetricOrdering.Unsorted,
											icon: "table",
											children: "Table order",
											innerRef,
										},
									]}
								/>
							)}
						>
							Automatically selected when using column sorting
						</AutoTooltip>
					);
				}}
			</FormField>
			<ForEach collection={Array.from(metric?.keyMetricThreshold?.toArray() ?? [])} keyProvider={(x) => x[0]}>
				{([id, keyMetricThreshold]) => {
					return (
						<div className="flex items-end gap-4 mb-4">
							<FormField label="Key Metric Threshold" className="w-[160px]">
								{(filedProps) => (
									<Select
										{...filedProps}
										triggerDataAttrs={{
											"data-qualifier": "InsightPanel/SortingParameters/KeyMetricThreshold/Trigger",
										}}
										data-qualifier="InsightPanel/SortingParameters/KeyMetricThreshold/Dropdown"
										options={keyMetricSelectionEntities}
										value={keyMetricThreshold.thresholdType ?? null}
										onChange={(newThresholdType) => {
											if (newThresholdType) {
												onChangeKeyMetricThreshold(
													id,
													newThresholdType === "MANUAL"
														? {
																thresholdType: newThresholdType,
																thresholdValue: 0,
															}
														: {
																thresholdType: newThresholdType,
															},
												);
											}

											if (newThresholdType === "MANUAL") {
												onCommitKeyThreshold();
											}
										}}
									/>
								)}
							</FormField>

							{keyMetricThreshold.thresholdType === "MANUAL" ? (
								<NumberInput
									data-qualifier="InsightPanel/SortingParameters/KeyMetricThreshold/Manual/Value"
									value={keyMetricThreshold.thresholdValue ?? null}
									rightContent={<Icon icon="Percentile" />}
									placeholder="0"
									onChange={(newThresholdValue) => {
										trackMixPanelEvent("portfolio.bulkAnalysis.sidePanel.keyMetric.threshold", null);
										onChangeKeyMetricThreshold(id, {
											...keyMetricThreshold,
											thresholdType: "MANUAL",
											thresholdValue: newThresholdValue ?? undefined,
										});

										onCommitKeyThreshold();
									}}
									className="flex-1"
								/>
							) : (
								<Controller
									value={keyMetricThreshold}
									onChange={(newThreshold) => {
										if (newThreshold) {
											onChangeKeyMetricThreshold(id, newThreshold);
										}

										if (metric?.key) {
											onCommitKeyThreshold();
										}
									}}
								>
									{({ value, onChange, onCommit }) => (
										<Controller value={false}>
											{({ value: isLoading, onChange: setIsLoading }) => (
												<Select
													innerRef={(e) =>
														e?.setAttribute(
															"data-qualifier",
															`InsightPanel/SortingParameters/KeyMetricThreshold/${value.thresholdType}/Trigger`,
														)
													}
													data-qualifier={`InsightPanel/SortingParameters/KeyMetricThreshold/${value.thresholdType}/Dropdown`}
													options={availableEntitiesByGroup}
													enableSearch
													value={value.thresholdEntity?.identifier ?? null}
													className="flex-1"
													disabled={isLoading}
													onChange={(val) => {
														if (!val) {
															return;
														}
														const newVal = {
															thresholdType: value.thresholdType,
															thresholdEntity: availableEntitiesMap.get(val),
															thresholdValue: undefined,
														};
														onChange(newVal);
														(async () => {
															if (!metric) {
																return;
															}
															setIsLoading(true);
															try {
																const key = Array.from(metric.keyMetricThreshold?.keys() ?? []).at(0);
																const newKeyMetricThreshold = handleMapTruthy(metric.keyMetricThreshold).set(
																	key ?? generateUniqueDOMId(),
																	newVal,
																);
																const { thresholdValue } =
																	await bulkAnalysisRepository.bulkAnalysisThresholdForMetric.fetchQuery({
																		metricKey: metric?.key,
																		metricThresholdEntity: newKeyMetricThreshold?.first()?.thresholdEntity,
																		metricReferenceType: metric?.referenceType,
																		metricReferenceEntity: metric.referenceEntity,
																		metricParameters: metric.metricParameters,
																	});
																onCommit({ ...newVal, thresholdValue });
															} catch (err) {
																console.error(err);
																platformToast({
																	children: "Unable to compute threshold value, please re-try",
																	icon: "Icon-full-error",
																	severity: "error",
																});
															} finally {
																setIsLoading(false);
															}
														})().catch(noop);
													}}
												/>
											)}
										</Controller>
									)}
								</Controller>
							)}
							<IconButton
								data-qualifier="InsightPanel/SortingParameters/KeyMetricThreshold/Delete"
								icon="Delete"
								onClick={() => {
									onRemoveKeyMetricThreshold(id);
									onCommitKeyThreshold();
								}}
								size={18}
								color={themeCSSVars.palette_P400}
								className="ml-auto"
							/>
						</div>
					);
				}}
			</ForEach>
			{(!metric?.keyMetricThreshold?.size || metric?.keyMetricThreshold?.size === 0) && (
				<FormField label="Key Metric Threshold" className="w-[160px]">
					<Button palette="secondary" className="w-fit mt-1" size="small" onClick={onAddKeyMetricThreshold}>
						<Icon icon="Outline1" />
					</Button>
				</FormField>
			)}
		</>
	);
}

type RichMetricValues = Omit<MetricValues, "taxonomy"> & {
	classification?: {
		classificationUuid: string;
		name: string;
	};
	assetClassCategory?: string;
	option?: string;
	taxonomy: Array<{ entry: string; parent?: string }>;
	riskModelExposureType?: string;
	riskModelExposureAssetClass?: string;
	riskModelSecondLevelExposureAssetClass?: string;
	riskModelSecondLevelExposureType?: string;
};

type HierarchicalMetrics = DiscriminateHierarchicalNodeOption<
	{ key: string },
	{ key: FieldKey; metricParameters?: MetricParameters }
>;

function generateHierarchicalMetrics(richMetric: RichMetricValues): HierarchicalOption<HierarchicalMetrics> {
	switch (richMetric.key) {
		case "TAG_EXPOSURE_OPTION": {
			return {
				label: richMetric.option!,
				value: {
					type: "leaf",
					key: richMetric.key,
					metricParameters: {
						classificationUuid: richMetric.classification!.classificationUuid,
						option: richMetric.option,
					},
				},
			};
		}
		case "SCORE_AVERAGE": {
			return {
				label: richMetric.classification!.name,
				value: {
					type: "leaf",
					key: richMetric.key,
					metricParameters: {
						classificationUuid: richMetric.classification!.classificationUuid,
					},
				},
			};
		}
		case "RISK_MODEL_EXPOSURE_AC": {
			return {
				label: richMetric.riskModelSecondLevelExposureAssetClass ?? richMetric.riskModelExposureAssetClass!,
				value: {
					type: "leaf",
					key: richMetric.key,
					metricParameters: {
						riskModelExposureAssetClass: richMetric.riskModelExposureAssetClass,
						riskModelExposureType: richMetric.riskModelExposureType,
						riskModelSecondLevelExposureAssetClass: richMetric.riskModelSecondLevelExposureAssetClass,
						riskModelSecondLevelExposureType: richMetric.riskModelSecondLevelExposureType,
					},
				},
			};
		}
		default: {
			return {
				label: t(`TABLE.HEADERS.${richMetric.key!}`),
				value: { key: richMetric.key!, type: "leaf" },
			};
		}
	}
}

type MinimumPath = {
	entry: string;
	parent?: string;
};

function recreateTreePath(pathNodes: Array<MinimumPath>, path: Array<MinimumPath> = []): Array<MinimumPath> {
	if (pathNodes.length === 0) {
		return path;
	}

	const parentNode = path.at(-1);
	const rootIndex = pathNodes.findIndex((x) => {
		if (!parentNode) {
			return x.entry && x.parent == null;
		}
		return parentNode.entry === x.parent;
	});

	if (rootIndex === -1) {
		console.error("missing node");
		return [];
	}

	path.push(pathNodes[rootIndex]);
	pathNodes.splice(rootIndex, 1);
	return recreateTreePath(pathNodes, path);
}

// remapping gone to far on line 1772 a node is treated as leaf, maybe it can be leave as node and pass with a Map
function createTreeHierarchicalOption(flatNode: RichMetricValues[]): HierarchicalOption<HierarchicalMetrics>[] {
	const root: HierarchicalOption<HierarchicalMetrics>[] = [];
	for (let i = 0; i < flatNode.length - 1; i++) {
		const node = flatNode[i];
		const taxonomy = node.taxonomy ?? [];
		if (taxonomy.length === 0) {
			if (node.key) {
				root.push(generateHierarchicalMetrics(node));
			}
		}

		if (taxonomy.length > 0) {
			const trackingIndex: number[] = [];
			const orderedTaxonomyPath = recreateTreePath([...taxonomy]);
			orderedTaxonomyPath.forEach((item, index) => {
				const currentNode = trackingIndex.reduce<
					HierarchicalOption<HierarchicalMetrics>[] | HierarchicalOption<HierarchicalMetrics>
				>((acc, nodeIndex) => {
					if (Array.isArray(acc)) {
						return acc[nodeIndex];
					}

					return acc.children![nodeIndex];
				}, root);

				const children = Array.isArray(currentNode) ? currentNode : currentNode.children;
				if (children) {
					const nodeIndex = children.findIndex((x) => x.value.key === item.entry);

					if (nodeIndex === -1) {
						children.push({
							label: metricTaxonomyLabel[item.entry as MetricValueTaxonomy] ?? item.entry,
							value: { key: item.entry, type: "node" },
							children: [],
						});
						trackingIndex.push(children.length - 1);
					}

					if (nodeIndex === -1 && index === orderedTaxonomyPath.length - 1) {
						const indexOfChildren = children.findIndex((x) => x.value.key === item.entry);
						children[indexOfChildren].children?.push(generateHierarchicalMetrics(node));
					}

					if (nodeIndex > -1 && index !== orderedTaxonomyPath.length - 1) {
						trackingIndex.push(nodeIndex);
					}

					if (nodeIndex > -1 && index === orderedTaxonomyPath.length - 1) {
						if (
							item.parent === "RISK_MODEL" &&
							(item.entry === "Macro Asset Class" || item.entry === "Micro Asset Class" || item.entry === "Geography")
						) {
							const metric = generateHierarchicalMetrics(node);
							const childrenIndex = children[nodeIndex].children?.findIndex(
								(x) => x.value.key === node.riskModelExposureAssetClass,
							);

							if (childrenIndex !== undefined && childrenIndex > -1) {
								const childNode = children[nodeIndex].children?.at(childrenIndex);
								children[nodeIndex].children?.splice(childrenIndex, 1, {
									label: metric.label,
									value: metric.value,
									disabled: metric.disabled,
									children: childNode?.children ?? [],
								});
							}
						} else {
							children[nodeIndex].children?.push(generateHierarchicalMetrics(node));
						}
					}
				}
			});
		}
	}

	return root;
}

const metricTaxonomyLabel: Record<MetricValueTaxonomy | "TAG_EXPOSURE_OPTION", string> = {
	CUSTOM_SCORES_AND_METRICS: "Custom scores & metrics",
	EFFICIENCY_RATIO: "Efficiency ratio",
	EX_ANTE_METRICS: "Ex ante metrics",
	EX_ANTE_VOLATILITY: "Ex ante volatility",
	EXPOSURES: "Exposure",
	HISTORICAL_VAR: "Historical VaR",
	MAX_DRAWDOWN: "Max drawdown",
	PARAMETRIC_VAR: "Parametric VaR",
	PERFORMANCE: "Performance",
	REALISED_METRICS: "Realised metrics",
	RISK_MODEL: "Risk model",
	SORTINO: "Sortino",
	VOLATILITY: "Volatility",
	TAG_EXPOSURE_OPTION: "Tags",
	ALPHA: "Alpha",
	BETA: "Beta",
	CALMAR_RATIO: "Calmar ratio",
	CORRELATION: "Correlation",
	UPSIDE_CAPTURE_RATIO: "Upside capture ratio",
	DOWNSIDE_CAPTURE_RATIO: "Downside capture ratio",
	SHARPE_RATIO: "Sharpe ratio",
	BATTING_AVERAGE: "Batting average",
	MAX_DRAWDOWN_TIME_TO_RECOVERY: "Max drawdown Time to Recovery",
};

export default PortfolioInsightPanel;
